import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, X, Plus, Minus, Trash2, ArrowRight, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/components/CartContext";
import { API } from "@/lib/api";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, clearCart, totalItems, totalPrice } = useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/cart`, {
        items: items.map((i) => ({ package_id: i.package_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: "",
        name: "",
      });
      if (res.data.url) {
        clearCart();
        window.location.href = res.data.url;
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Cart Button (floating) */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-[#D4AF37] text-[#050505] w-14 h-14 rounded-full shadow-lg shadow-black/30 flex items-center justify-center hover:bg-[#E5C07B] transition-colors"
        data-testid="cart-button"
      >
        <ShoppingCart className="w-6 h-6" />
        {totalItems > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
            {totalItems}
          </span>
        )}
      </button>

      {/* Drawer Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[100]" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#121212] border-l border-white/10 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="font-heading text-xl tracking-tight uppercase text-white">Your Cart</h2>
                {totalItems > 0 && <span className="text-xs text-zinc-500">({totalItems} item{totalItems !== 1 ? "s" : ""})</span>}
              </div>
              <button onClick={() => setIsOpen(false)} className="text-zinc-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-grow overflow-y-auto p-6 space-y-4">
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingCart className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 text-sm">Your cart is empty</p>
                  <button onClick={() => setIsOpen(false)} className="text-[#D4AF37] text-sm mt-2 hover:underline">
                    Continue shopping
                  </button>
                </div>
              ) : (
                items.map((item) => (
                  <div key={item.package_id} className="bg-[#1A1A1A] border border-white/10 p-4 rounded-sm" data-testid={`cart-item-${item.package_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-white text-sm font-medium">{item.name}</p>
                        <p className="text-[#D4AF37] text-xs mt-1">${item.price.toFixed(2)} AUD each</p>
                      </div>
                      <button onClick={() => removeItem(item.package_id)} className="text-zinc-600 hover:text-red-400 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.package_id, item.quantity - 1)}
                          className="w-7 h-7 border border-white/20 rounded-sm text-white hover:border-[#D4AF37] flex items-center justify-center"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-heading text-lg text-white w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.package_id, item.quantity + 1)}
                          className="w-7 h-7 border border-white/20 rounded-sm text-white hover:border-[#D4AF37] flex items-center justify-center"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-white font-semibold text-sm">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="p-6 border-t border-white/10 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm uppercase tracking-wider">Subtotal</span>
                  <span className="font-heading text-2xl text-[#D4AF37]">${totalPrice.toFixed(2)} AUD</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-500 text-xs">
                  <Tag className="w-3 h-3" />
                  <span>Discount codes can be applied at checkout</span>
                </div>
                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full btn-gold h-14 rounded-sm text-sm tracking-[0.15em] uppercase"
                  data-testid="cart-checkout-button"
                >
                  {loading ? "Redirecting..." : "Proceed To Checkout"}
                </Button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full h-10 text-xs tracking-[0.1em] uppercase text-zinc-400 hover:text-[#D4AF37] transition-colors"
                  data-testid="continue-shopping-button"
                >
                  Continue Shopping
                </button>
                <p className="text-zinc-600 text-[10px] text-center">Shipping calculated at checkout for physical items</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
